import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import assessmentService from '../services/assessmentService';
import authService from '../services/authService';

interface Subject {
  subject_id: number;
  subject_name: string;
}

interface PupilRow {
  pupil_id: number;
  admission_number: string;
  first_name: string;
  last_name: string;
  scores: { [subject_id: number]: number | null };
}

export const EnterScores: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const [searchParams] = useSearchParams();
  
  const numericClassId = parseInt(classId || '0', 10);
  const term = parseInt(searchParams.get('term') || '1', 10);
  const typeId = parseInt(searchParams.get('type') || '1', 10); // 1 = Mid-Term, 2 = End of Term
  const yearId = parseInt(searchParams.get('year') || '1', 10);

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [grid, setGrid] = useState<PupilRow[]>([]);
  const [statuses, setStatuses] = useState<{ [subjectId: number]: string }>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // References to input elements for arrow key navigation
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const maxScore = 100.00;

  const currentUser = authService.getCurrentUser();
  const permissions = currentUser?.permissions || [];
  const hasPermission = (perm: string) => permissions.includes(perm);

  useEffect(() => {
    const fetchGrid = async () => {
      try {
        const data = await assessmentService.getTermMarksGrid(numericClassId, term, typeId, yearId);
        if (data) {
          setSubjects(data.subjects);
          setGrid(data.grid);
          setStatuses(data.statuses || {});
        }
      } catch (err: any) {
        setError('Failed to load assessment marks. Please confirm the server is running.');
      } finally {
        setLoading(false);
      }
    };

    fetchGrid();
  }, [numericClassId, term, typeId, yearId]);

  const handleScoreChange = (pupilId: number, subjectId: number, val: string) => {
    setGrid((prev) =>
      prev.map((r) => {
        if (r.pupil_id === pupilId) {
          const parsed = val === '' ? null : parseFloat(val);
          return {
            ...r,
            scores: { ...r.scores, [subjectId]: parsed }
          };
        }
        return r;
      })
    );
    setSaveSuccess(false);
  };

  const triggerAutoSave = async (currentGrid: PupilRow[]) => {
    // Collect all valid scores
    const payload: { pupil_id: number; subject_id: number; score: number }[] = [];
    let hasError = false;

    currentGrid.forEach(row => {
      Object.entries(row.scores).forEach(([subId, score]) => {
        if (score !== null) {
          if (score < 0 || score > maxScore) {
            hasError = true;
          } else {
            payload.push({
              pupil_id: row.pupil_id,
              subject_id: parseInt(subId, 10),
              score: score
            });
          }
        }
      });
    });

    if (hasError) {
      setError(`Validation Error: All scores must reside strictly between 0 and ${maxScore}.`);
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const success = await assessmentService.saveTermMarksGrid(
        numericClassId,
        term,
        typeId,
        yearId,
        payload
      );

      if (success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (err: any) {
      setError('Background sync failure: failed to save marks to server (locked results might be protected).');
    } finally {
      setSaving(false);
    }
  };

  const handleWorkflow = async (subjectId: number, action: 'submit' | 'validate' | 'lock' | 'unlock') => {
    setError(null);
    setSaving(true);
    try {
      let success = false;
      if (action === 'submit') {
        success = await assessmentService.submitResults(numericClassId, subjectId, term, typeId, yearId);
      } else if (action === 'validate') {
        success = await assessmentService.validateResults(numericClassId, subjectId, term, typeId, yearId);
      } else if (action === 'lock') {
        success = await assessmentService.lockResults(numericClassId, subjectId, term, typeId, yearId);
      } else if (action === 'unlock') {
        success = await assessmentService.unlockResults(numericClassId, subjectId, term, typeId, yearId);
      }

      if (success) {
        const data = await assessmentService.getTermMarksGrid(numericClassId, term, typeId, yearId);
        if (data) {
          setSubjects(data.subjects);
          setGrid(data.grid);
          setStatuses(data.statuses || {});
        }
      } else {
        setError(`Failed to perform workflow action: ${action}`);
      }
    } catch (err: any) {
      setError('Failed to update result status.');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, colIndex: number) => {
    let targetRowIndex: number | null = null;
    let targetColIndex: number | null = null;

    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      e.preventDefault();
      if (rowIndex < grid.length - 1) {
        targetRowIndex = rowIndex + 1;
        targetColIndex = colIndex;
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (rowIndex > 0) {
        targetRowIndex = rowIndex - 1;
        targetColIndex = colIndex;
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (colIndex < subjects.length - 1) {
        targetRowIndex = rowIndex;
        targetColIndex = colIndex + 1;
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (colIndex > 0) {
        targetRowIndex = rowIndex;
        targetColIndex = colIndex - 1;
      }
    }

    if (targetRowIndex !== null && targetColIndex !== null) {
      const targetInput = inputRefs.current[`score-${targetRowIndex}-${targetColIndex}`];
      if (targetInput) {
        targetInput.focus();
        targetInput.select();
      }
    }
  };

  if (loading) {
    return (
      <div className="flex h-60 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-xs font-semibold text-slate-500 text-helper">Loading grade sheet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Roster header and back action */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <Link to={`/classes/${numericClassId}`} className="text-xs font-semibold text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2">
            &larr; Return to Dashboard
          </Link>
          <h1 className="text-page-header">Continuous Assessment Master Grid</h1>
          <p className="text-helper mt-0.5">
            Session: <strong className="text-slate-800">{typeId === 1 ? `Term ${term} Mid-Term` : `End of Term ${term}`}</strong> | (Max Appraisal: <strong className="text-slate-800">{maxScore} marks</strong>)
          </p>
        </div>

        {/* Sync Status Banner */}
        <div className="flex items-center gap-2.5 text-xs font-semibold text-slate-500 border border-slate-100 rounded-xl px-4 py-2 bg-slate-50/50">
          {saving && (
            <span className="text-indigo-600 flex items-center gap-1.5 animate-pulse">
              <span className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-600 border-t-transparent"></span>
              Syncing...
            </span>
          )}
          {saveSuccess && (
            <span className="text-emerald-600 flex items-center gap-1">
              <span>💚</span> Grades synced.
            </span>
          )}
          {!saving && !saveSuccess && (
            <span className="text-slate-400">💤 Auto-saves on blur</span>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-helper text-center">
          ⚠️ {error}
        </div>
      )}

      {/* Spreadsheet Container with Freezing and Styling */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto overflow-y-hidden max-w-full">
          <table className="w-full border-collapse border-spacing-0" style={{ minWidth: 'max-content' }}>
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {/* Fixed column pupil name */}
                <th className="p-3 text-left text-card-title text-slate-400 uppercase tracking-wider sticky left-0 bg-slate-50 z-10 shadow-[2px_0_5px_rgba(0,0,0,0.03)] border-r border-slate-200" style={{ width: '220px' }}>
                  Student Name
                </th>
                {subjects.map(s => (
                  <th key={s.subject_id} className="p-3 text-center text-card-title text-slate-400 uppercase tracking-wider border-b border-slate-200" style={{ width: '160px' }}>
                    {s.subject_name}
                  </th>
                ))}
              </tr>
              <tr className="bg-slate-100/50 border-b border-slate-200">
                <th className="p-2.5 text-left text-[9px] text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50 z-10 shadow-[2px_0_5px_rgba(0,0,0,0.03)] border-r border-slate-200">
                  Workflow States
                </th>
                {subjects.map(s => {
                  const status = statuses[s.subject_id] || 'Draft';
                  return (
                    <th key={`status-${s.subject_id}`} className="p-2.5 text-center border-b border-slate-200">
                      <div className="flex flex-col gap-1.5 items-center">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${
                          status === 'Draft' ? 'bg-slate-100 text-slate-600 border-slate-200' :
                          status === 'Submitted' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          status === 'Validated' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          'bg-purple-50 text-purple-700 border-purple-200'
                        }`}>
                          {status}
                        </span>
                        
                        <div className="flex gap-1">
                          {status === 'Draft' && hasPermission('enter_results') && (
                            <button 
                              onClick={() => handleWorkflow(s.subject_id, 'submit')}
                              className="px-2 py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded text-[9px] font-bold shadow-sm transition-all"
                            >
                              Submit
                            </button>
                          )}
                          {status === 'Submitted' && hasPermission('validate_results') && (
                            <button 
                              onClick={() => handleWorkflow(s.subject_id, 'validate')}
                              className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded text-[9px] font-bold shadow-sm transition-all"
                            >
                              Validate
                            </button>
                          )}
                          {status === 'Validated' && hasPermission('lock_results') && (
                            <button 
                              onClick={() => handleWorkflow(s.subject_id, 'lock')}
                              className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[9px] font-bold shadow-sm transition-all"
                            >
                              Lock
                            </button>
                          )}
                          {status === 'Locked' && hasPermission('lock_results') && (
                            <button 
                              onClick={() => handleWorkflow(s.subject_id, 'unlock')}
                              className="px-2 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded text-[9px] font-bold shadow-sm transition-all"
                            >
                              Unlock
                            </button>
                          )}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {grid.map((row, rowIndex) => (
                <tr key={row.pupil_id} className="hover:bg-slate-50/40 transition-colors">
                  {/* Sticky left student name column */}
                  <td className="p-3 text-table-body font-bold text-indigo-950 sticky left-0 bg-white z-10 shadow-[2px_0_5px_rgba(0,0,0,0.03)] border-r border-slate-200">
                    {row.first_name} {row.last_name}
                  </td>
                  {subjects.map((s, colIndex) => {
                    const score = row.scores[s.subject_id];
                    const isOutOfRange = score !== null && score !== undefined && (score < 0 || score > maxScore);
                    const isLocked = statuses[s.subject_id] && statuses[s.subject_id] !== 'Draft';

                    return (
                      <td key={s.subject_id} className="p-1.5 text-center">
                        <input
                          ref={(el) => { inputRefs.current[`score-${rowIndex}-${colIndex}`] = el; }}
                          type="number"
                          step="0.05"
                          min="0"
                          max={maxScore}
                          inputMode="decimal"
                          pattern="[0-9]*"
                          value={score === null || score === undefined ? '' : score}
                          onChange={(e) => handleScoreChange(row.pupil_id, s.subject_id, e.target.value)}
                          onBlur={() => triggerAutoSave(grid)}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                          disabled={!!isLocked}
                          className={`w-full text-center py-2 px-1 rounded-lg border font-bold text-nav focus:ring-2 outline-none transition-all ${
                            isLocked 
                              ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed' 
                              : isOutOfRange 
                              ? 'bg-rose-50 border-rose-400 text-rose-700 focus:ring-rose-500/20' 
                              : 'bg-slate-50 hover:bg-slate-100/60 focus:bg-white border-slate-200 focus:border-indigo-600 focus:ring-indigo-500/20'
                          }`}
                          placeholder="--"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Roster controls action row */}
      <div className="flex justify-end gap-2 pt-2">
        <button 
          onClick={() => triggerAutoSave(grid)} 
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-50 text-nav flex items-center justify-center gap-2"
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
              <span>Syncing Grid...</span>
            </>
          ) : (
            <>
              <span>💾</span>
              <span>Verify & Sync Score Sheet</span>
            </>
          )}
        </button>
      </div>

    </div>
  );
};

export default EnterScores;
